import { Button } from "@devhop/ui";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowLeftIcon, BanknoteIcon, CalendarDaysIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type {
  BookingSlot,
  PublicRoom,
  TimetableItem,
} from "../../domain/contracts/public-booking";
import { publicBookingApi } from "../api/client";
import { AlertBanner } from "../ui/AlertBanner";
import { PublicRoomBookingForm } from "../ui/PublicRoomBookingForm";
import { PublicTimetable } from "../ui/PublicTimetable";
import { StudioPayBalancePanel } from "../ui/StudioPayBalancePanel";

export function PublicBookingPage() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isStudio = pathname.startsWith("/studio");
  const [studioTab, setStudioTab] = useState<"book" | "pay">("book");
  const [items, setItems] = useState<TimetableItem[]>([]);
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [rangeStart, setRangeStart] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd"),
  );
  const [rangeEnd, setRangeEnd] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd"),
  );
  const [bookingSlot, setBookingSlot] = useState<BookingSlot | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchTimetable = useCallback(async () => {
    const { data } = await publicBookingApi.getTimetable({
      start: rangeStart,
      end: rangeEnd,
    });
    setItems(data);
  }, [rangeStart, rangeEnd]);

  const fetchRooms = useCallback(async () => {
    const { data } = await publicBookingApi.getRooms();
    setRooms(data);
  }, []);

  useEffect(() => {
    void fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    void fetchTimetable();
  }, [fetchTimetable]);

  useEffect(() => {
    const interval = setInterval(() => {
      void fetchTimetable();
      void fetchRooms();
    }, 10_000);
    return () => clearInterval(interval);
  }, [fetchTimetable, fetchRooms]);

  const handleRangeChange = useCallback((start: string, end: string) => {
    setRangeStart(start);
    setRangeEnd(end);
  }, []);

  const handleSlotSelect = useCallback((slot: BookingSlot) => {
    setBookingSlot(slot);
    setFormOpen(true);
  }, []);

  const todayYMD = format(new Date(), "yyyy-MM-dd");
  const todayItems = items.filter((i) => i.date === todayYMD);

  return (
    <div className="relative flex flex-1 flex-col p-4">
      {/* Floating Back Button */}
      <Button
        type="button"
        size="lg"
        variant="outline"
        onClick={() => navigate({ to: "/app/dashboard" })}
        className="fixed right-4 bottom-4 z-50 shadow-lg backdrop-blur-sm"
      >
        <ArrowLeftIcon className="size-6" />
      </Button>

      {isStudio ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={studioTab === "book" ? "default" : "outline"}
            size="sm"
            onClick={() => setStudioTab("book")}
          >
            <CalendarDaysIcon className="mr-2 size-4" />
            ຕາຕະລາງ ແລະ ຈອງ
          </Button>
          <Button
            type="button"
            variant={studioTab === "pay" ? "default" : "outline"}
            size="sm"
            onClick={() => setStudioTab("pay")}
          >
            <BanknoteIcon className="mr-2 size-4" />
            ຊຳລະຕໍ່
          </Button>
        </div>
      ) : null}

      <div className="mb-2 flex flex-col gap-2">
        {!isStudio || studioTab === "book" ? (
          <AlertBanner todayItems={todayItems} />
        ) : null}
      </div>
      <div className="mt-4 flex-1">
        {!isStudio || studioTab === "book" ? (
          <PublicTimetable
            initialView="month"
            items={items}
            rooms={rooms}
            onSlotSelect={handleSlotSelect}
            onRangeChange={handleRangeChange}
          />
        ) : (
          <StudioPayBalancePanel />
        )}
      </div>
      <PublicRoomBookingForm
        slot={bookingSlot}
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={fetchTimetable}
      />
    </div>
  );
}
